import {
  Badge,
  Box,
  Button,
  Flex,
  Heading,
  HStack,
  Input,
  Spinner,
  Text,
  VStack,
} from '@chakra-ui/react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  ChevronDownIcon,
  ChevronRightIcon,
  FolderOpenIcon,
  GlobeIcon,
  LockIcon,
  SearchIcon,
  TrashIcon,
  XIcon,
} from 'lucide-react'
import { useMemo, useState } from 'react'
import {
  type SavedPlaygroundDetail,
  type SavedPlaygroundSummary,
  savedPlaygroundsApi,
} from '@/lib/api'
import { useColorModeValue } from '~/color-mode'
import { toaster } from '~/toaster'

interface PlaygroundGroup {
  key: string
  name: string
  createdByEmail: string | undefined
  isOwner: boolean
  latest: SavedPlaygroundSummary
  versions: SavedPlaygroundSummary[]
}

interface LoadPlaygroundDialogProps {
  onClose: () => void
  onLoad: (playground: SavedPlaygroundDetail) => void
}

function LoadPlaygroundDialog({ onClose, onLoad }: LoadPlaygroundDialogProps) {
  const queryClient = useQueryClient()
  const emptyStateIconColor = useColorModeValue('#777672', '#BBB9B5')
  const [searchQuery, setSearchQuery] = useState('')
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set())

  // Fetch saved playgrounds
  const { data, isLoading } = useQuery({
    queryKey: ['saved-playgrounds', { search: searchQuery }],
    queryFn: () =>
      savedPlaygroundsApi.list({
        search: searchQuery || undefined,
        pageSize: 100,
      }),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => savedPlaygroundsApi.delete(id),
    onSuccess: () => {
      toaster.create({
        title: 'Playground deleted',
        type: 'success',
      })
      queryClient.invalidateQueries({ queryKey: ['saved-playgrounds'] })
    },
    onError: (error: Error) => {
      toaster.create({
        title: 'Failed to delete playground',
        description: error.message,
        type: 'error',
      })
    },
  })

  const loadMutation = useMutation({
    mutationFn: (id: string) => savedPlaygroundsApi.get(id),
    onSuccess: (playground) => {
      onLoad(playground)
      onClose()
    },
    onError: (error: Error) => {
      toaster.create({
        title: 'Failed to load playground',
        description: error.message,
        type: 'error',
      })
    },
  })

  const handleDelete = (e: React.MouseEvent, playground: SavedPlaygroundSummary) => {
    e.stopPropagation()
    if (confirm(`Delete "${playground.name}" (v${playground.version})?`)) {
      deleteMutation.mutate(playground.id)
    }
  }

  const handleLoad = (playground: SavedPlaygroundSummary) => {
    loadMutation.mutate(playground.id)
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })
  }

  const playgrounds = data?.playgrounds || []

  const groups = useMemo<PlaygroundGroup[]>(() => {
    const map = new Map<string, SavedPlaygroundSummary[]>()
    for (const pg of playgrounds) {
      const key = `${pg.name}::${pg.createdByEmail ?? ''}`
      const list = map.get(key)
      if (list) {
        list.push(pg)
      } else {
        map.set(key, [pg])
      }
    }

    const result: PlaygroundGroup[] = []
    for (const [key, versions] of map) {
      versions.sort((a, b) => b.version - a.version)
      const latest = versions[0]
      result.push({
        key,
        name: latest.name,
        createdByEmail: latest.createdByEmail,
        isOwner: latest.isOwner,
        latest,
        versions,
      })
    }

    result.sort(
      (a, b) => new Date(b.latest.updatedAt).getTime() - new Date(a.latest.updatedAt).getTime(),
    )
    return result
  }, [playgrounds])

  const toggleGroup = (key: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev)
      if (next.has(key)) {
        next.delete(key)
      } else {
        next.add(key)
      }
      return next
    })
  }

  return (
    <Box
      position="fixed"
      inset={0}
      bg="blackAlpha.600"
      zIndex={2000}
      onClick={onClose}
      overflow="auto"
    >
      <Box
        position="relative"
        mx="auto"
        my={8}
        bg="bg.panel"
        borderRadius="lg"
        w="90vw"
        maxW="600px"
        maxH="80vh"
        overflow="hidden"
        display="flex"
        flexDirection="column"
        onClick={(e) => e.stopPropagation()}
      >
        <Flex justify="space-between" align="center" p={4} borderBottomWidth="1px">
          <Heading size="md">Load Playground</Heading>
          <Button size="sm" variant="ghost" onClick={onClose}>
            <XIcon size={16} />
          </Button>
        </Flex>

        <Box p={4} borderBottomWidth="1px">
          <HStack>
            <SearchIcon size={16} />
            <Input
              placeholder="Search playgrounds..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              size="sm"
              flex={1}
            />
          </HStack>
        </Box>

        <Box flex={1} overflow="auto" p={4}>
          {isLoading ? (
            <Flex justify="center" py={8}>
              <Spinner />
            </Flex>
          ) : groups.length === 0 ? (
            <VStack py={8} gap={2}>
              <FolderOpenIcon size={48} color={emptyStateIconColor} />
              <Text color="content.secondary" textAlign="center">
                {searchQuery
                  ? 'No playgrounds found matching your search'
                  : 'No saved playgrounds yet'}
              </Text>
            </VStack>
          ) : (
            <VStack gap={2} align="stretch">
              {groups.map((group) => {
                const hasMultiple = group.versions.length > 1
                const isExpanded = expandedGroups.has(group.key)
                const olderCount = group.versions.length - 1

                return (
                  <Box key={group.key}>
                    {/* Group header row - shows latest version */}
                    <Box
                      p={3}
                      borderWidth="1px"
                      borderRadius={isExpanded ? 'md md 0 0' : 'md'}
                      cursor="pointer"
                      _hover={{
                        bg: 'gray.50',
                        _dark: { bg: 'gray.700' },
                      }}
                      onClick={() => handleLoad(group.latest)}
                      opacity={loadMutation.isPending ? 0.5 : 1}
                    >
                      <Flex justify="space-between" align="flex-start">
                        <HStack gap={2} flex={1}>
                          {hasMultiple && (
                            <Box
                              as="button"
                              onClick={(e: React.MouseEvent) => {
                                e.stopPropagation()
                                toggleGroup(group.key)
                              }}
                              p={0.5}
                              borderRadius="sm"
                              _hover={{
                                bg: 'gray.200',
                                _dark: {
                                  bg: 'gray.600',
                                },
                              }}
                              display="flex"
                              alignItems="center"
                            >
                              {isExpanded ? (
                                <ChevronDownIcon size={14} />
                              ) : (
                                <ChevronRightIcon size={14} />
                              )}
                            </Box>
                          )}
                          <Box flex={1}>
                            <HStack gap={2} mb={1}>
                              <Text fontWeight="medium">{group.name}</Text>
                              <Badge size="sm" colorPalette="brand">
                                v{group.latest.version}
                                {olderCount > 0 && ` + ${olderCount} older`}
                              </Badge>
                              {group.latest.isPublic ? (
                                <Badge size="sm" colorPalette="green" variant="subtle">
                                  <HStack gap={1}>
                                    <GlobeIcon size={10} />
                                    <span>Public</span>
                                  </HStack>
                                </Badge>
                              ) : (
                                <Badge size="sm" colorPalette="gray" variant="subtle">
                                  <HStack gap={1}>
                                    <LockIcon size={10} />
                                    <span>Private</span>
                                  </HStack>
                                </Badge>
                              )}
                            </HStack>
                            <HStack gap={3} fontSize="xs" color="content.secondary">
                              {group.latest.customerName && (
                                <Text>Customer: {group.latest.customerName}</Text>
                              )}
                              <Text>
                                {group.latest.isOwner
                                  ? 'Created by you'
                                  : `By ${group.latest.createdByEmail || 'Unknown'}`}
                              </Text>
                              <Text>{formatDate(group.latest.updatedAt)}</Text>
                            </HStack>
                          </Box>
                        </HStack>
                        {group.latest.isOwner && (
                          <Button
                            size="xs"
                            variant="ghost"
                            colorPalette="red"
                            onClick={(e) => handleDelete(e, group.latest)}
                            disabled={deleteMutation.isPending}
                          >
                            <TrashIcon size={14} />
                          </Button>
                        )}
                      </Flex>
                    </Box>

                    {/* Expanded sub-rows for older versions */}
                    {isExpanded &&
                      group.versions.slice(1).map((version, idx) => (
                        <Box
                          key={version.id}
                          pl={8}
                          pr={3}
                          py={2}
                          borderWidth="0 1px 1px 1px"
                          borderRadius={idx === group.versions.length - 2 ? '0 0 md md' : undefined}
                          cursor="pointer"
                          bg="gray.50"
                          _dark={{
                            bg: 'gray.750',
                          }}
                          _hover={{
                            bg: 'gray.100',
                            _dark: {
                              bg: 'gray.700',
                            },
                          }}
                          onClick={() => handleLoad(version)}
                          opacity={loadMutation.isPending ? 0.5 : 1}
                        >
                          <Flex justify="space-between" align="center">
                            <HStack gap={2} flex={1}>
                              <Text fontSize="xs" color="content.secondary">
                                {idx === group.versions.length - 2 ? '└─' : '├─'}
                              </Text>
                              <Badge size="sm" colorPalette="gray">
                                v{version.version}
                              </Badge>
                              {version.isPublic ? (
                                <Badge size="sm" colorPalette="green" variant="subtle">
                                  <HStack gap={1}>
                                    <GlobeIcon size={10} />
                                    <span>Public</span>
                                  </HStack>
                                </Badge>
                              ) : (
                                <Badge size="sm" colorPalette="gray" variant="subtle">
                                  <HStack gap={1}>
                                    <LockIcon size={10} />
                                    <span>Private</span>
                                  </HStack>
                                </Badge>
                              )}
                              <Text fontSize="xs" color="content.secondary">
                                {formatDate(version.updatedAt)}
                              </Text>
                            </HStack>
                            {version.isOwner && (
                              <Button
                                size="xs"
                                variant="ghost"
                                colorPalette="red"
                                onClick={(e) => handleDelete(e, version)}
                                disabled={deleteMutation.isPending}
                              >
                                <TrashIcon size={14} />
                              </Button>
                            )}
                          </Flex>
                        </Box>
                      ))}
                  </Box>
                )
              })}
            </VStack>
          )}
        </Box>

        <Flex justify="flex-end" p={4} borderTopWidth="1px">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
        </Flex>
      </Box>
    </Box>
  )
}

export default LoadPlaygroundDialog
